// 稽核的白話呈現（SPEC #vms-web-817 稽核頁；VMS UX 評審 2026-10-13 P1-9、B-10、P0-7）：
//   - 動作中文分組：影像存取｜設備控制｜設定變更｜權限｜帳號｜匯出｜帳單｜其他；組內才列單項（譯名），不列英文動作代碼
//   - 每列一行摘要「<操作者顯示名> <做了什麼> <對象完整名>」；細節展開成白話欄位表，原始 JSON 收在「技術細節」
//   - 日期範圍：今天｜昨天｜過去 7 天（預設）｜自訂；API 只有往前翻的 cursor（= 早於），起點在前端截斷
import type { AuditEvent } from './planeApi';
export type { AuditEvent };
import { nearStamp, stamp } from './timefmt';
import { durationText } from './duration';
import catalogLabels from './kinio_ui_labels.json';

export type AuditGroup = 'media' | 'control' | 'settings' | 'perms' | 'account' | 'export' | 'billing' | 'other';
export const AUDIT_GROUPS: AuditGroup[] = ['media', 'control', 'settings', 'perms', 'account', 'export', 'billing', 'other'];

const RULES: [AuditGroup, RegExp][] = [
  ['export', /^(export\.|recording\.export|audit\.export|vms\.export)/],
  ['media', /^(media\.|recording\.(playback|download|read)|vms\.offline\.(live|playback))/],
  ['control', /^(device\.(command|control|rpc|reboot)|ptz\.|siren\.|vms\.offline\.control|fleet\.(console|command)|vms\.alarm_state)/],
  ['billing', /^billing\./],
  ['account', /^(auth\.|mfa\.|api_key\.|account\.|app_link\.|org_link\.|service_token\.|member\.mfa_reset|vms\.offline\.login)/],
  [
    'perms',
    /^(grant\.|role\.|node_assignment\.|node\.self_assign|delegation|change_request\.|share\.|member|home\.member\.|home\.owner|home\.unassigned|org\.owner_transfer|takeover)/,
  ],
  [
    'settings',
    /^(device\.(settings|location)|recording_policy\.|site\.|org\.(create|delete|security_policy|tree_settings|rename|node_enroll|inventory)|node\.|home\.|room\.|ai\.|backup\.|onvif\.|rule|thing\.|fleet\.local_credential|vms\.(ai|floorplan|device_links)|view\.)/,
  ],
];

/** 動作屬於哪一組。 */
export function auditGroupOf(action: string): AuditGroup {
  for (const [g, re] of RULES) if (re.test(action)) return g;
  return 'other';
}

/** 動作的 i18n key（a_<action 底線>）。 */
export const actionKey = (a: string) => 'audit.a_' + a.replace(/\./g, '_');

export type AuditRange = 'today' | 'yesterday' | '7d' | 'custom';

/** 日期範圍 → [from, to)（毫秒；本機時區的日界）。custom 用 YYYY-MM-DD 兩天（含頭含尾）。 */
export function rangeOf(r: AuditRange, now = new Date(), custom?: { from: string; to: string }): { from: number; to: number } {
  const day0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const DAY = 86400_000;
  switch (r) {
    case 'today':
      return { from: day0, to: now.getTime() + 60_000 };
    case 'yesterday':
      return { from: day0 - DAY, to: day0 };
    case 'custom': {
      const f = custom?.from ? new Date(custom.from + 'T00:00:00').getTime() : day0 - 6 * DAY;
      const t = custom?.to ? new Date(custom.to + 'T00:00:00').getTime() + DAY : now.getTime() + 60_000;
      return { from: f, to: Math.max(f, t) };
    }
    default:
      return { from: day0 - 6 * DAY, to: now.getTime() + 60_000 };
  }
}

/** 名稱查表（操作者、設備、家戶、節點）；查不到回 undefined，畫面再決定寫什麼。 */
export interface AuditNames {
  user(id: string): string | undefined;
  device(ref: string): string | undefined;
  home(id: string): string | undefined;
  node(id: string): string | undefined;
}

/** 這一筆的「對象」：設備 > 人 > 家戶 > 節點（resource 先、detail 後）。 */
export function auditTarget(e: Pick<AuditEvent, 'resource' | 'detail' | 'home_id'>, n: AuditNames): { kind: 'device' | 'user' | 'home' | 'node'; name?: string } | null {
  const r = { ...(e.detail ?? {}), ...(e.resource ?? {}) } as Record<string, unknown>;
  const s = (k: string) => (typeof r[k] === 'string' && r[k] ? (r[k] as string) : undefined);
  // 舊紀錄（2026-10-14 前）的 fleet.command / thing.assigned / thing.moved 只有 {kind: thing, id}：也當設備（SPEC 稽核動作對照表）
  const dev = s('device_ref') ?? s('device') ?? s('sensor_ref') ?? (r.kind === 'device' || r.kind === 'thing' ? s('id') : undefined);
  // 本地回放 / 即時的 resource 是錄影主機＋channel（0 起）：寫到通道「lstw-16ch / CH2」，不只寫主機（第七輪 B7-09）
  const ch = typeof r.channel === 'number' && Number.isInteger(r.channel) && r.channel >= 0 && dev && !dev.includes(':ch') ? r.channel : undefined;
  if (dev && ch !== undefined) {
    const child = n.device(`${dev}:ch${ch}`);
    const parent = n.device(dev);
    return { kind: 'device', name: child ?? (parent ? `${parent} / CH${ch + 1}` : undefined) };
  }
  if (dev) return { kind: 'device', name: n.device(dev) };
  const user = s('user_id') ?? s('target_user_id');
  if (user) return { kind: 'user', name: n.user(user) };
  const home = s('home_id') ?? (r.kind === 'home' ? s('id') : undefined);
  if (home) return { kind: 'home', name: n.home(home) };
  const node = s('node_id') ?? (r.kind === 'node' ? s('id') : undefined);
  if (node) return { kind: 'node', name: n.node(node) };
  return null;
}

type T = (k: string, o?: Record<string, unknown>) => string;

/**
 * 一行摘要：有專屬句型（audit.s_<action>）就用它，沒有就「<誰> <動作譯名>：<對象>」；動作沒有譯名時寫組名（不露英文代碼）。
 * 純讀取的本地 RPC 平台已不寫成設備命令（SPEC「讀取不記成設備命令」）；本地回放是 recording.playback（detail.source = local），摘要寫出來源。
 */
export function auditSummary(t: T, e: AuditEvent, who: string, n: AuditNames): string {
  const tg = auditTarget(e, n);
  const target = tg ? (tg.name ?? t('audit.unknown_' + tg.kind)) : '';
  const src = typeof e.detail?.source === 'string' ? t('audit.src_' + e.detail.source, { defaultValue: '' }) : '';
  const vars: Record<string, string | number> = { who, target, src, ...actionVars(t, e, n) };
  const k = sentenceKey(e, String(vars.name ?? ''), String(vars.cmdName ?? ''));
  const sentence = t('audit.s_' + k, { ...vars, defaultValue: '' });
  if (sentence) return sentence;
  const named = t(actionKey(e.action), { defaultValue: '' });
  // 「其他」只能當篩選的分組名，不當一列的動作（SPEC 稽核「動作名與雜訊」；第二輪 B2-25）：沒有譯名又不屬於任何組 → 通用句，代碼在技術細節
  if (!named && auditGroupOf(e.action) === 'other') return target ? t('audit.line_generic_target', { who, target }) : t('audit.line_generic', { who });
  const what = named || t('audit.g_' + auditGroupOf(e.action));
  return target ? t('audit.line_target', { who, what, target }) : t('audit.line', { who, what });
}

/** 動作對照表的句型 key（SPEC #vms-web-817「動作對照表」）：同一類的幾個動作共用一句。 */
function sentenceKey(e: AuditEvent, name: string, cmdName = ''): string {
  const a = e.action;
  if (a.startsWith('qa.org_case.')) return 'qa_org_case';
  if (/(^|\.)aid_allocate$/.test(a)) return 'aid_allocate';
  if (a === 'fleet.console.session_closed') return 'fleet_console_close';
  if (a === 'thing.update' && !name) return 'thing_update_plain';
  // 唯讀命令（ch.list 這類）寫白話「讀了 NVR631 的通道清單」，不寫「下了命令」（第六輪 B6-11；平台不記唯讀命令之前的前端防線）
  if ((a === 'device.command' || a === 'device.rpc') && readCommand(cmdOf(e))) return 'device_read';
  // 命令名稱照 op 查設備目錄（SPEC 動作對照表 device.command；platform 2571097e 起 detail.op）：讀屬性「讀了 X 的〈名稱〉」、
  // 改屬性「改了 X 的「〈名稱〉」」、命令 / RPC「對 X 下了命令「〈名稱〉」」；查不到名稱照「下了命令」
  if ((a === 'device.command' || a === 'device.rpc') && cmdName) {
    const op = cmdOp(e);
    return op === 'get' ? 'device_read' : op === 'set' ? 'device_set_named' : 'device_command_named';
  }
  // 回放寫看了哪段（「看了 lstw-16ch / CH2 10-14 15:24 起的雲端錄影」；第七輪 B7-09）
  if (a === 'recording.playback' && playbackFrom(e)) return 'recording_playback_at';
  // 「看即時影像」（media.live）跟有時長的觀看同一個句型「Eddy 看了 lstw-16ch / CH3 的即時影像」（第九輪 B9-05）
  if (a === 'media.live') return 'media_watched_nodur';
  return a.replace(/\./g, '_');
}

/** 回放從哪個時間起（detail.from RFC 3339，或舊列的 from_us 微秒）；沒有 = null。 */
function playbackFrom(e: AuditEvent): number | null {
  const d = (e.detail ?? {}) as Record<string, unknown>;
  if (typeof d.from === 'string' && Number.isFinite(Date.parse(d.from))) return Date.parse(d.from);
  if (typeof d.from_us === 'number' && d.from_us > 0) return Math.floor(d.from_us / 1000);
  return null;
}

const cmdOf = (e: AuditEvent): string => {
  const d = (e.detail ?? {}) as Record<string, unknown>;
  const v = d.command ?? d.method ?? d.id;
  return typeof v === 'string' ? v : '';
};

const cmdOp = (e: AuditEvent): string => {
  const v = ((e.detail ?? {}) as Record<string, unknown>).op;
  return typeof v === 'string' ? v : '';
};

const CATALOG = catalogLabels as { attrs: Record<string, string>; commands: Record<string, string> };

/**
 * device.command 的命令名稱（SPEC #vms-web-817 動作對照表）：op = set / get 查設備目錄的 attrs、invoke 查 commands、rpc 查 RPC 字串表
 * （audit.rpc_<方法>）；沒有 op 的舊紀錄先查兩張目錄表、再查 RPC 表；都查不到回空字串（照寫「下了命令」）。
 * 名稱：繁中用目錄原文；其他語系用 kinio_model 譯文（label_id = key），沒有譯文就不寫名稱（不在英文畫面夾中文）。
 */
export function commandName(t: T, cmd: string, op: string): string {
  if (!cmd) return '';
  const zh = t('audit.catalog_zh_hant') === 'yes';
  const fromCatalog = (table: Record<string, string>): string => {
    const label = table[cmd];
    if (!label) return '';
    if (zh) return label;
    return t(cmd, { ns: 'kinio_model', keySeparator: false, nsSeparator: false, defaultValue: '' });
  };
  const rpc = () => t('audit.rpc_' + cmd.replace(/\./g, '_'), { defaultValue: '' });
  if (op === 'set' || op === 'get') return fromCatalog(CATALOG.attrs);
  if (op === 'invoke') return fromCatalog(CATALOG.commands);
  if (op === 'rpc') return rpc();
  return fromCatalog(CATALOG.attrs) || fromCatalog(CATALOG.commands) || rpc();
}

/**
 * 設備的唯讀命令：跟平台的唯讀清單同一份（SPEC #vms-web-817「讀取不記成設備命令」）。平台改了之後不會再記，
 * 這裡只為了舊紀錄（與平台還沒部署時）寫白話；不在清單上的照「下了命令」。
 */
export const READ_COMMANDS = new Set([
  'dhcp.pending', 'pb.days', 'pb.list', 'pb.thumb', 'pb.timeline', 'pb.events', 'pb.exportSize',
  'ch.list', 'ch.get', 'sys.info', 'sys.time', 'sys.caps', 'kinio.status', 'alarm.active', 'deterrence.status', 'stream.info',
  'wifi.state', 'wifi.found', 'wifi.clients', 'hdd.info', 'rec.info', 'rec.status', 'info.record', 'snap.list', 'kinio_ptz.list_presets',
]);
export function readCommand(cmd: string): boolean {
  return READ_COMMANDS.has(cmd);
}

/** 動作對照表各句要的變數（沒有就空字串，句子照樣通順）。 */
function actionVars(t: T, e: AuditEvent, n: AuditNames): Record<string, string | number> {
  const d = (e.detail ?? {}) as Record<string, unknown>;
  const r = (e.resource ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const paren = (v: string) => (v ? t('audit.paren', { v }) : '');
  const to = str(d.to);
  const place = str(d.to) || str(d.home_id);
  const fromAt = Date.parse(str(d.from));
  const toAt = Date.parse(str(d.to));
  const clipSec = Number.isFinite(fromAt) && Number.isFinite(toAt) && toAt > fromAt ? Math.round((toAt - fromAt) / 1000) : 0;
  const cmd = str(d.command);
  return {
    mode: d.mode === 'write' ? t('audit.mode_write') : d.mode === 'read' ? t('audit.mode_read') : '',
    cmd: cmd ? t('audit.fleet_cmd_' + cmd, { defaultValue: t('audit.fleet_cmd_other') }) : t('audit.fleet_cmd_other'),
    name: str(d.display_name) || str(d.name),
    place: place ? (n.home(place) ?? n.node(place) ?? t('audit.unknown_home')) : t('audit.unknown_home'),
    state: e.action === 'vms.alarm_state' && to ? t('alarm.state_' + to, { defaultValue: to }) : e.action === 'org.status' && to ? t('audit.org_status_' + to, { defaultValue: to }) : '',
    n: Array.isArray(d.cameras) ? d.cameras.length : 0,
    dur: clipSec ? paren(durationText(t, clipSec)) : '',
    mac: str(d.mac) ? paren(t('audit.mac_tail', { v: str(d.mac).slice(-5) })) : '',
    serial: paren(str(d.serial)),
    model: str(r.model) || str(d.model),
    provider: str(d.provider) ? (n.device(str(d.provider)) ?? '') : '',
    // 唯讀命令讀的是什麼（ch.list →「通道清單」）；沒有譯名寫「資料」
    rcmd: (() => {
      // op = get（讀屬性）用目錄名稱；唯讀 RPC 用 rcmd 表
      const named = cmdOp(e) === 'get' ? commandName(t, cmdOf(e), 'get') : '';
      return named ? t('audit.quoted', { v: named }) : t('audit.rcmd_' + cmdOf(e).replace(/\./g, '_'), { defaultValue: t('audit.rcmd_other') });
    })(),
    cmdName: e.action === 'device.command' || e.action === 'device.rpc' ? commandName(t, cmdOf(e), cmdOp(e) || (e.action === 'device.rpc' ? 'rpc' : '')) : '',
    at: (() => {
      const f = playbackFrom(e);
      return f === null ? '' : nearStamp(f, e.created_at);
    })(),
  };
}

/** 一次觀看（開始＋結束配成一列）：「Eddy 看了 3KPT 的即時影像 2 分 5 秒」（第四輪 R4-P2-3）。 */
export function auditWatched(t: T, e: AuditEvent, who: string, n: AuditNames, sec: number): string {
  const tg = auditTarget(e, n);
  const target = tg ? (tg.name ?? t('audit.unknown_' + tg.kind)) : '';
  // sec < 0 = 時長不知道（只有結束、沒有開始也沒帶時長）：不寫時長，也不寫成「結束看」（第七輪 B7-08）；
  // 時長用全站同一個函式（不到 1 秒「不到 1 秒」，第十二輪 B12-07）
  const dur = sec < 0 ? '' : durationText(t, sec);
  // 本地錄影的回放隧道（detail.kind = playback）：「看了 X 的本地錄影 N 秒」；有播放起點（detail.from）時照雲端回放寫
  // 「看了 NVR631 / CH4 21:29 起的本地錄影 29 秒」（同一天時:分、不同天月-日 時:分；第十二輪 B12-04）
  const kind = (e.detail as Record<string, unknown> | undefined)?.kind;
  const from = kind === 'playback' ? playbackFrom(e) : null;
  const key = kind === 'playback' ? (from !== null ? 'audit.s_media_replayed_at' : 'audit.s_media_replayed') : 'audit.s_media_watched';
  const at = from !== null ? nearStamp(from, e.created_at) : '';
  return t(sec < 0 ? key + '_nodur' : key, { who, target, dur, at });
}

/** 配不到結束的本地錄影回放開始：「Eddy 開始看 NVR631 的本地錄影」（後面接「（沒有結束紀錄）」；第十一輪 B11-01，照即時影像的寫法）。 */
export function auditReplayStarted(t: T, e: AuditEvent, who: string, n: AuditNames): string {
  const tg = auditTarget(e, n);
  const target = tg ? (tg.name ?? t('audit.unknown_' + tg.kind)) : '';
  // 有播放起點：「開始看 NVR631 / CH4 21:29 起的本地錄影」（B12-04）
  const from = playbackFrom(e);
  if (from !== null) return t('audit.s_media_replay_started_at', { who, target, at: nearStamp(from, e.created_at) });
  return t('audit.s_media_replay_started', { who, target });
}

/** 合併用的「同一件事」：同一個人、同一個動作、同一個對象。 */
function sameKey(e: AuditEvent): string {
  const r = { ...(e.detail ?? {}), ...(e.resource ?? {}) } as Record<string, unknown>;
  // 回放：同一人、同一台、同一個來源就算同一件事（每次回放的 session id 都不同，5 分鐘內 5 列沒合併；第七輪 B7-09）
  if (e.action === 'recording.playback')
    return `${e.actor_user_id ?? ''}|${e.action}|${String(r.device_ref ?? r.device ?? '')}|${String(r.channel ?? '')}|${String(r.source ?? '')}`;
  const obj = ['device_ref', 'device', 'user_id', 'target_user_id', 'home_id', 'node_id', 'id'].map((k) => (typeof r[k] === 'string' ? r[k] : '')).join('|');
  return `${e.actor_user_id ?? ''}|${e.action}|${obj}`;
}

/** 合併的時間窗（5 分鐘）。 */
export const AUDIT_MERGE_MS = 5 * 60_000;
/** 同一次觀看的多筆「結束看」相隔多久以內算同一次 */
export const END_DUP_MS = 120_000;

/**
 * 稽核清單的雜訊收斂（第二輪 B2-25）：
 *   - 同一人、同一台、同一個動作，前後兩筆相隔 5 分鐘內 → 併成一列「× N 次」（清單由新到舊，鏈狀合併）
 *   - 一次觀看只留一列：有「開始看即時影像」（media.session.start）時，同一人、同一台前後 2 分鐘內的 media.live（「影像存取」）不另列
 */
export type AuditRowData = {
  e: AuditEvent;
  count: number;
  watchedSec?: number;
  noEnd?: boolean;
  /** 這一列是哪一種觀看（即時 / 本地錄影回放）；不是觀看的列沒有（篩選「回放錄影」「看即時影像」用，第十一輪 B11-01） */
  watch?: 'live' | 'playback';
  /** 合併列（count > 1）併進來的每一筆，新到舊（第一筆 = e）；展開逐次列出、技術細節列全部（第十二輪 B12-01） */
  members?: AuditEvent[];
};

/** 觀看類的動作：選了這幾個單項時不帶 action 向平台拿，用同一個合併函式合成列後再篩（SPEC 第十一輪收尾 B11-01）。 */
export const WATCH_ACTIONS = new Set(['media.live', 'media.session.start', 'media.session.end', 'recording.playback']);

/** 合併後的一列是否屬於篩選的觀看動作：「回放錄影」= 雲端回放的列＋本地錄影回放；即時三個動作 = 即時觀看的列。 */
export function rowMatchesAction(row: AuditRowData, action: string): boolean {
  if (action === 'recording.playback') return row.e.action === 'recording.playback' || row.watch === 'playback';
  if (WATCH_ACTIONS.has(action)) return row.watch === 'live';
  return row.e.action === action;
}

/** 本地錄影回放的開始（recording.playback 帶 session_id、source = local；雲端回放一次一筆，不是開始）。 */
function isLocalPlayback(x: AuditEvent): boolean {
  const d = (x.detail ?? {}) as Record<string, unknown>;
  return x.action === 'recording.playback' && !!d.session_id && (d.source === 'local' || d.kind === 'playback');
}

/** 觀看的種類（detail.kind：live / playback / ai）；本地回放的開始一律 playback；沒有 kind 的舊列 = ''（跟哪一種都配得上）。 */
function watchKindOf(x: AuditEvent): string {
  if (x.action === 'recording.playback') return 'playback';
  const k = (x.detail as Record<string, unknown> | undefined)?.kind;
  return typeof k === 'string' ? k : '';
}

export function collapseAudit(events: AuditEvent[], now = Date.now()): AuditRowData[] {
  // 同一人同一台的「開始看 / 結束看」配成一列「看了 N 秒」（第四輪 R4-P2-3）：清單新到舊，結束那筆往後找最近的開始
  // 「同一台」：resource 帶 channel 的當通道（<主機>:ch<n>）；一邊只有主機時以主機比（第八輪 B8-05：本地回放開始帶通道、結束只有主機，配不起來）
  const devFull = (x: AuditEvent) => {
    const r = { ...(x.detail ?? {}), ...(x.resource ?? {}) } as Record<string, unknown>;
    const ref = String(r.device_ref ?? r.device ?? '');
    const ch = r.channel;
    return typeof ch === 'number' && Number.isInteger(ch) && ch >= 0 && ref && !ref.includes(':ch') ? `${ref}:ch${ch}` : ref;
  };
  const hostOf = (ref: string) => (ref.includes(':ch') ? ref.slice(0, ref.lastIndexOf(':ch')) : ref);
  const sameDev = (a: AuditEvent, b: AuditEvent) => {
    const x = devFull(a);
    const y = devFull(b);
    if (x === y) return true;
    return hostOf(x) === hostOf(y) && (!x.includes(':ch') || !y.includes(':ch'));
  };
  const paired = new Set<AuditEvent>();
  const watched = new Map<AuditEvent, number>();
  const sid = (x: AuditEvent) => String((x.detail as Record<string, unknown> | undefined)?.session_id ?? '');
  // 開始那筆：即時影像是 media.session.start；本地錄影回放隧道記成 recording.playback（source = local），兩筆都帶 session_id。
  // 雲端回放（source = cloud，session_id 是 pbs-…）一次一筆，不是開始（第十一輪 B11-01）
  const isStart = (x: AuditEvent) => x.action === 'media.session.start' || isLocalPlayback(x);
  // 同一次觀看的多筆「結束看」（主 / 子碼流各一條、重連）：跟已經配到的結束相隔 2 分鐘內的併掉（第六輪 B6-11「一次開始、多次結束」）
  const absorbed = new Set<AuditEvent>();
  const pairedEnds: AuditEvent[] = [];
  const near = (a: AuditEvent, b: AuditEvent) => Math.abs(Date.parse(a.created_at) - Date.parse(b.created_at)) <= END_DUP_MS;
  // 同一人、同一台、**同一種觀看**（第十一輪 B11-01：本地回放沒帶通道時以主機比，同一台 NVR 接著開影像牆，
  // 即時的結束把本地回放的開始與結束都吸走，「全部動作」少一列；種類看 detail.kind，沒有 kind 的舊列兩種都可以）
  const sameKind = (a: AuditEvent, b: AuditEvent) => {
    const x = watchKindOf(a);
    const y = watchKindOf(b);
    return !x || !y || x === y;
  };
  const same = (a: AuditEvent, b: AuditEvent) => a.actor_user_id === b.actor_user_id && sameDev(a, b) && sameKind(a, b);
  // 結束那筆配到的開始（併成一列時通道從有帶 channel 的那筆取）
  const startOf = new Map<AuditEvent, AuditEvent>();
  /** 併進這一列（結束那筆）的開始 */
  const merged = new Map<AuditEvent, AuditEvent[]>();
  const absorb = (end: AuditEvent, start: AuditEvent) => {
    paired.add(start);
    merged.set(end, [...(merged.get(end) ?? []), start]);
  };
  // 有自己結束的開始（同一個 session_id 有一筆 media.session.end）：不被別次的時長窗併走（第十二輪 B12-06）
  const endSids = new Set(events.filter((x) => x.action === 'media.session.end' && sid(x)).map(sid));
  /** 結束那筆自己的開始（同一個 session_id、還沒配到、同一人同一台同一種）；沒有 = -1 */
  const ownStart = (e: AuditEvent, i: number): number => {
    const k = sid(e);
    if (!k) return -1;
    for (let j = i + 1; j < events.length; j++) {
      const s = events[j];
      if (isStart(s) && !paired.has(s) && sid(s) === k && same(s, e)) return j;
    }
    return -1;
  };
  events.forEach((e, i) => {
    if (e.action !== 'media.session.end') return;
    const own = ownStart(e, i);
    // 跟已配到的結束相隔 2 分鐘內的另一筆結束：只有對不到自己的開始時才當同一次觀看併掉
    // （第十二輪 B12-06：影像牆 ICE 失敗重連，舊 session 的結束被新那一列吸走，舊的開始留成「沒有結束紀錄」）
    if (own < 0 && pairedEnds.some((x) => same(x, e) && near(x, e))) {
      absorbed.add(e);
      return;
    }
    if (own >= 0) {
      const s = events[own];
      const sec = (Date.parse(e.created_at) - Date.parse(s.created_at)) / 1000;
      if (sec >= 0 && sec <= 12 * 3600) {
        absorb(e, s);
        startOf.set(e, s);
        watched.set(e, Math.round(sec));
        pairedEnds.push(e);
      }
    }
    for (let j = i + 1; j < events.length && !watched.has(e); j++) {
      const s = events[j];
      if (!same(s, e)) continue;
      if (s.action === 'media.session.end') {
        if (near(s, e)) continue; // 同一次觀看的另一筆結束（稍早一點）：照樣往前找開始
        break; // 中間還有另一次觀看
      }
      if (isStart(s) && !paired.has(s) && (!sid(e) || !sid(s) || sid(e) === sid(s) || events.slice(i + 1, j).some((x) => x.action === 'media.session.end' && sid(x) === sid(s)))) {
        const sec = (Date.parse(e.created_at) - Date.parse(s.created_at)) / 1000;
        if (sec >= 0 && sec <= 12 * 3600) {
          absorb(e, s);
          startOf.set(e, s);
          watched.set(e, Math.round(sec));
          pairedEnds.push(e);
        }
        break;
      }
    }
    // 開始那筆不在這一頁（太早、或被篩掉）：結束那筆自己帶時長（detail.duration_s）→ 一樣寫「看了 N 秒」，不留單獨的「結束看」（第五輪 B5-22）
    const d = (e.detail as Record<string, unknown> | undefined)?.duration_s;
    if (!watched.has(e) && typeof d === 'number' && Number.isFinite(d) && d >= 0) {
      watched.set(e, Math.round(d));
      pairedEnds.push(e);
    }
    // 有時長（帶 duration_s，或上面配到開始）的「看了」已經知道幾點開始看：開始時間前後 2 分鐘到結束之間**所有**還沒配到的開始都併進來
    // （第七輪 B7-08：開始與結束的 session_id 不同也算）
    // （第八輪 B8-05：本地回放一次開了兩條隧道 →「19:27 …本地錄影」沒時長＋「19:28 …本地錄影 13 秒」兩列；「17:46 開始看」沒被吸收）
    const known = startOf.get(e);
    const dur = typeof d === 'number' && Number.isFinite(d) && d >= 0 ? d : known ? (Date.parse(e.created_at) - Date.parse(known.created_at)) / 1000 : -1;
    if (dur >= 0) {
      const endAt = Date.parse(e.created_at);
      const startAt = endAt - dur * 1000;
      for (let j = i + 1; j < events.length; j++) {
        const s = events[j];
        if (!same(s, e)) continue;
        const at = Date.parse(s.created_at);
        if (at < startAt - END_DUP_MS) break;
        if (!isStart(s) || paired.has(s) || at > endAt) continue;
        if (sid(s) && sid(s) !== sid(e) && endSids.has(sid(s))) continue; // 它有自己的結束，是另一次觀看
        absorb(e, s);
        if (!startOf.has(e)) startOf.set(e, s);
      }
    }
    // 只有結束、沒有開始也沒有時長：寫「看了 X 的即時影像」（時長不知道），不留單獨的「結束看」
    if (!watched.has(e)) {
      watched.set(e, -1);
      pairedEnds.push(e);
    }
  });
  const sessions = events.filter((e) => e.action === 'media.session.start');
  const dupLive = (e: AuditEvent) =>
    e.action === 'media.live' &&
    sessions.some((s) => {
      const a = { ...(s.detail ?? {}), ...(s.resource ?? {}) } as Record<string, unknown>;
      const b = { ...(e.detail ?? {}), ...(e.resource ?? {}) } as Record<string, unknown>;
      const dev = (x: Record<string, unknown>) => (x.device_ref ?? x.device ?? '') as string;
      return s.actor_user_id === e.actor_user_id && dev(a) === dev(b) && Math.abs(Date.parse(s.created_at) - Date.parse(e.created_at)) <= 120_000;
    });
  // 有時長的觀看（即時 / 本地回放，配到開始或帶 duration_s）一次一列，不跟別次合併成「× N 次」（第十二輪 B12-01：
  // 兩次本地回放 29 秒＋3 秒合成一列，第一次的時間與時長消失）；5 分鐘合併只用在沒有時長的列
  const solo = (e: AuditEvent) => (watched.get(e) ?? -1) >= 0;
  const out: { e: AuditEvent; count: number; last: number; key: string; members: AuditEvent[] }[] = [];
  for (const e of events) {
    if (dupLive(e) || paired.has(e) || absorbed.has(e)) continue;
    const key = solo(e) ? '' : sameKey(e);
    const at = Date.parse(e.created_at);
    const prev = out[out.length - 1];
    if (prev && key && prev.key === key && Math.abs(prev.last - at) <= AUDIT_MERGE_MS) {
      prev.count += 1;
      prev.last = at;
      prev.members.push(e);
      continue;
    }
    out.push({ e, count: 1, last: at, key, members: [e] });
  }
  // 還沒配到的「開始看」：同一人同一台之後還有別的觀看紀錄、或已經超過 12 小時 → 寫「（沒有結束紀錄）」（第八輪 B8-05）；
  // 本地回放的開始一樣（第十一輪 B11-01：「開始看 NVR631 的本地錄影（沒有結束紀錄）」）
  const noEnd = (e: AuditEvent, idx: number) =>
    isStart(e) &&
    (now - Date.parse(e.created_at) > 12 * 3600_000 ||
      events.slice(0, idx).some((x) => x !== e && same(x, e) && (x.action === 'media.session.start' || x.action === 'media.session.end' || x.action === 'recording.playback')));
  // 併成一列時通道從有帶 channel 的那筆取（「看了 lstw-16ch / CH2 的本地錄影 13 秒」）；播放起點（detail.from）也一樣：
  // 結束那筆沒帶、開始那筆（本地回放）有帶就用開始的（SPEC：開始或結束哪一筆有 from 就用哪一筆，B12-04）
  const withChannel = (e: AuditEvent): AuditEvent => {
    const s = startOf.get(e);
    if (!s) return e;
    let out = e;
    const ch = (s.resource as Record<string, unknown> | undefined)?.channel ?? (s.detail as Record<string, unknown> | undefined)?.channel;
    const mine = (e.resource as Record<string, unknown> | undefined)?.channel;
    if (typeof ch === 'number' && typeof mine !== 'number' && !devFull(e).includes(':ch')) out = { ...out, resource: { ...(out.resource ?? {}), channel: ch } };
    const sFrom = (s.detail as Record<string, unknown> | undefined)?.from;
    if (isLocalPlayback(s) && playbackFrom(e) === null && typeof sFrom === 'string' && Number.isFinite(Date.parse(sFrom)))
      out = { ...out, detail: { ...(out.detail ?? {}), from: sFrom } };
    return out;
  };
  // 時長不知道的「看了」合併成多列時照樣用「看了」的句子（× N 次），不退回「結束看」；
  // 有時長的合併成多列（3KPT「16:17 結束看 × 2 次」）也用「看了」的句子，不寫時長
  // 這一列是哪一種觀看：本地回放（開始或 kind = playback 的結束、或併進來的開始是本地回放）/ 即時
  const watchOf = (e: AuditEvent): AuditRowData['watch'] => {
    if (isLocalPlayback(e)) return 'playback';
    if (e.action === 'media.session.end') return watchKindOf(e) === 'playback' || (merged.get(e) ?? []).some(isLocalPlayback) ? 'playback' : 'live';
    if (e.action === 'media.session.start' || e.action === 'media.live') return 'live';
    return undefined;
  };
  return out.map(({ e, count, members }) => {
    const watch = watchOf(e);
    const w = { ...(watch ? { watch } : {}), ...(count > 1 ? { members } : {}) };
    if (watched.has(e)) return { e: withChannel(e), count, watchedSec: count === 1 ? watched.get(e) : -1, ...w };
    if (count === 1 && noEnd(e, events.indexOf(e))) return { e, count, noEnd: true, ...w };
    return { e, count, ...w };
  });
}

const PATHS = new Set(['lan', 'direct', 'relay']);
/** media hub 的觀看結束原因（platform internal/media：removeLocked / EndSession）；字串表 audit.reason_<值>。 */
export const END_REASONS = new Set(['client_stop', 'client_gone', 'device_gone', 'viewer_limit', 'token_expired', 'access_revoked', 'authorization_unavailable', 'api_delete', 'timeout']);

/** 展開後的白話欄位：已知的鍵用譯名、值換成名稱；其他鍵不列（原始 JSON 在「技術細節」）。 */
export function auditFields(t: T, e: AuditEvent, n: AuditNames): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  const all = { ...(e.resource ?? {}), ...(e.detail ?? {}) } as Record<string, unknown>;
  const name = (k: string, v: string): string => {
    // 設備跟標題同一個名字，帶通道時寫到通道「NVR631 / CH4」（第十二輪 B12-02）
    if (k === 'device_ref' || k === 'device') {
      const tg = auditTarget(e, n);
      return (tg?.kind === 'device' && tg.name) || n.device(v) || t('audit.unknown_device');
    }
    if (k === 'user_id' || k === 'target_user_id') return n.user(v) ?? t('iam.left_member');
    if (k === 'home_id') return n.home(v) ?? t('audit.unknown_home');
    if (k === 'node_id') return n.node(v) ?? t('audit.unknown_node');
    if (k === 'source') return t('audit.src_' + v, { defaultValue: v });
    // 連線方式（detail.path）：lan / direct / relay 寫中文（同 LIVE-08 三態）；unknown 不列（第十一輪 B11-02）
    if (k === 'path') return PATHS.has(v) ? t('live.path_' + v) : v === 'unknown' ? '' : v;
    // 結束原因（media hub 的 reason）走字串表；不認得的不列（原值在技術細節，第十二輪 B12-02）
    if (k === 'reason' && (e.action === 'media.session.end' || e.action === 'recording.playback')) return END_REASONS.has(v) ? t('audit.reason_' + v) : '';
    return v;
  };
  for (const k of ['device_ref', 'device', 'user_id', 'target_user_id', 'home_id', 'node_id', 'source', 'reason', 'keys', 'command', 'path']) {
    const v = all[k];
    if (v === undefined || v === null || v === '') continue;
    const val = Array.isArray(v) ? v.map(String).join(t('common.list_sep')) : typeof v === 'object' ? '' : name(k, String(v));
    if (val) out.push({ label: t('audit.f_' + k), value: val });
  }
  // 本地回放的播放起點（開隧道時從哪一刻開始看，B12-04）：寫完整時間；雲端回放的 from / to 照舊不列（標題已寫）
  const localWatch = isLocalPlayback(e) || (e.action === 'media.session.end' && watchKindOf(e) === 'playback');
  const from = localWatch ? playbackFrom(e) : null;
  if (from !== null) out.push({ label: t('audit.f_from'), value: stamp(from) });
  if (e.ip) out.push({ label: t('audit.f_ip'), value: e.ip });
  return out;
}
